// Generated by ts-to-zod
import type { JsonValue } from "@codemod.com/butterflow-scheduler/types";
import { z } from "zod";

export const RuntimeTypeSchema = z.union([
  z.literal("direct"),
  z.literal("docker"),
  z.literal("podman"),
]);

export const UseAstGrepSchema = z.object({
  include: z.array(z.string()).optional(),
  exclude: z.array(z.string()).optional(),
  base_path: z.string().optional(),
  config_file: z.string(),
});

export const JsonValueSchema: z.ZodSchema<JsonValue> = z.lazy(() =>
  z
    .union([
      z.number(),
      z.string(),
      z.boolean(),
      z.array(JsonValueSchema),
      z.record(JsonValueSchema),
    ])
    .nullable(),
);

export const TemplateUseSchema = z.object({
  template: z.string(),
  inputs: z.record(z.string()).optional(),
});

export const RuntimeSchema = z.object({
  type: RuntimeTypeSchema,
  image: z.string().optional().nullable(),
  working_dir: z.string().optional().nullable(),
  user: z.string().optional().nullable(),
  network: z.string().optional().nullable(),
  options: z.array(z.string()).optional().nullable(),
});

export const TemplateInputSchema = z.object({
  name: z.string(),
  type: z.string(),
  required: z.boolean().optional(),
  description: z.string().nullable(),
  default: z.string().nullable(),
});

export const TemplateOutputSchema = z.object({
  name: z.string(),
  value: z.string(),
  description: z.string().nullable(),
});

export const TaskStatusSchema = z.union([
  z.literal("Pending"),
  z.literal("Running"),
  z.literal("Completed"),
  z.literal("Failed"),
  z.literal("AwaitingTrigger"),
  z.literal("Blocked"),
  z.literal("WontDo"),
]);

export const UseCodemodSchema = z.object({
  source: z.string(),
  args: z.array(z.string()).optional(),
  env: z.record(z.string()).optional(),
  working_dir: z.string().optional(),
});

export const WorkflowStatusSchema = z.union([
  z.literal("Pending"),
  z.literal("Running"),
  z.literal("Completed"),
  z.literal("Failed"),
  z.literal("AwaitingTrigger"),
  z.literal("Canceled"),
]);

export const StrategyTypeSchema = z.literal("matrix");

export const TriggerTypeSchema = z.union([
  z.literal("automatic"),
  z.literal("manual"),
]);

export const StateSchemaTypeSchema = z.union([
  z.literal("array"),
  z.literal("object"),
  z.literal("string"),
  z.literal("number"),
  z.literal("boolean"),
]);

export const DiffOperationSchema = z.union([
  z.literal("Add"),
  z.literal("Update"),
  z.literal("Remove"),
  z.literal("Append"),
]);

export const TriggerSchema = z.object({
  type: TriggerTypeSchema,
});

export const NodeTypeSchema = z.union([
  z.literal("automatic"),
  z.literal("manual"),
]);

export const StrategySchema = z.object({
  type: StrategyTypeSchema,
  values: z.array(z.record(JsonValueSchema)).optional(),
  from_state: z.string().optional().nullable(),
});

export const StateSchemaPropertySchema = z.object({
  type: StateSchemaTypeSchema,
  description: z.string().optional().nullable(),
});

export const UseJsAstGrepSchema = z.object({
  js_file: z.string(),
  include: z.array(z.string()).optional(),
  exclude: z.array(z.string()).optional(),
  base_path: z.string().optional(),
  no_gitignore: z.boolean().optional(),
  include_hidden: z.boolean().optional(),
  max_threads: z.number().optional(),
  dry_run: z.boolean().optional(),
  language: z.string().optional(),
});

export const TaskSchema = z.object({
  id: z.string(),
  workflow_run_id: z.string(),
  node_id: z.string(),
  status: TaskStatusSchema,
  is_master: z.boolean(),
  master_task_id: z.string().optional().nullable(),
  matrix_values: z.record(JsonValueSchema).optional().nullable(),
  started_at: z.string().optional().nullable(),
  ended_at: z.string().optional().nullable(),
  error: z.string().optional().nullable(),
  logs: z.array(z.string()),
});

export const FieldDiffSchema = z.object({
  operation: DiffOperationSchema,
  value: JsonValueSchema.nullable(),
});

export const StepSchema = z
  .object({
    name: z.string(),
    env: z.record(z.string()).optional(),
  })
  .and(
    z.union([
      z.object({
        use: TemplateUseSchema,
      }),
      z.object({
        run: z.string(),
      }),
      z.object({
        "ast-grep": UseAstGrepSchema,
      }),
      z.object({
        "js-ast-grep": UseJsAstGrepSchema,
      }),
      z.object({
        codemod: UseCodemodSchema,
      }),
    ]),
  );

export const StateSchemaItemsSchema = z.object({
  type: StateSchemaTypeSchema,
  properties: z.record(StateSchemaPropertySchema).optional().nullable(),
});

export const TaskDiffSchema = z.object({
  task_id: z.string(),
  fields: z.record(FieldDiffSchema),
});

export const TemplateSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().optional().nullable(),
  runtime: RuntimeSchema.optional().nullable(),
  inputs: z.array(TemplateInputSchema),
  steps: z.array(StepSchema),
  outputs: z.array(TemplateOutputSchema).optional(),
  env: z.record(z.string()).optional(),
});

export const WorkflowRunDiffSchema = z.object({
  workflow_run_id: z.string(),
  fields: z.record(FieldDiffSchema),
});

export const StateSchemaSchema = z.object({
  name: z.string(),
  type: StateSchemaTypeSchema,
  items: StateSchemaItemsSchema.optional().nullable(),
  description: z.string().optional().nullable(),
});

export const NodeSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().optional().nullable(),
  type: NodeTypeSchema,
  depends_on: z.array(z.string()).optional(),
  trigger: TriggerSchema.optional().nullable(),
  strategy: StrategySchema.optional().nullable(),
  runtime: RuntimeSchema.optional().nullable(),
  steps: z.array(StepSchema),
  env: z.record(z.string()).optional(),
});

export const StateDiffSchema = z.object({
  workflow_run_id: z.string(),
  fields: z.record(FieldDiffSchema),
});

export const WorkflowStateSchema = z.object({
  schema: z.array(StateSchemaSchema),
});

export const ButterflowWorkflowSchema = z.object({
  version: z.string(),
  state: WorkflowStateSchema.optional().nullable(),
  templates: z.array(TemplateSchema).optional(),
  nodes: z.array(NodeSchema),
});

export const WorkflowRunSchema = z.object({
  id: z.string(),
  workflow: ButterflowWorkflowSchema,
  status: WorkflowStatusSchema,
  params: z.record(z.string()),
  tasks: z.array(z.string()),
  started_at: z.string(),
  ended_at: z.string().optional().nullable(),
  bundle_path: z.string().optional().nullable(),
});

export type ButterflowWorkflow = z.infer<typeof ButterflowWorkflowSchema>;
